// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { api } from '../services/api';
// Importing User type from the shared types file for central source of truth
import { User } from '../types/chat'; 

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  /**
   * Guest login with hotelId + surname + reservationId.
   * Returns either:
   * - { guests, reservation } when multiple guests are found (no tokens set)
   * - { user, accessToken, refreshToken, ... } when a single user is logged in
   */
  login: (hotelId: string, surname: string, reservationId: string) => Promise<any>;

  /**
   * Finalize guest selection when multiple guests match.
   * On success, stores tokens and user in context and returns full auth payload.
   */
  selectGuest: (
    hotelId: string,
    reservationId: string,
    profileId: string,
    surname?: string
  ) => Promise<any>;

  /**
   * Guest selection from HeyHotelLoginURL deep link (no surname validation).
   */
  selectGuestFromDeepLink: (
    hotelId: string,
    reservationId: string,
    profileId: string
  ) => Promise<any>;

  logout: () => void;
  register: (userData: any) => Promise<void>;
  refreshAccessToken: () => Promise<string>;
  /** Re-fetch /auth/profile and update the in-memory user (e.g. after completing guest info). */
  refreshUserProfile: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'REGISTER_FAILURE' }
  | { type: 'TOKEN_REFRESHED'; payload: { token: string; refreshToken: string } }
  | { type: 'USER_UPDATED'; payload: { user: User } };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isLoading: !!localStorage.getItem('token'), // Set to true if token exists
  isAuthenticated: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return { ...state, isLoading: false, isAuthenticated: false };
    case 'TOKEN_REFRESHED':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };
    case 'USER_UPDATED':
      return {
        ...state,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      };
    default:
      return state;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    if (state.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
      // Verify token and get user profile
      api
        .get('/auth/profile')
        .then((response) => {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: response.data,
              token: state.token!,
              refreshToken: state.refreshToken!,
            },
          });
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          dispatch({ type: 'LOGOUT' });
        });
    }
  }, [state.token, state.refreshToken]);

  /**
   * Guest login flow (surname + reservationId + hotelId).
   * Does NOT manage isLoading state – individual pages handle their own spinners.
   */
  const login = async (hotelId: string, surname: string, reservationId: string): Promise<any> => {
    const response = await api.post('/auth/login', {
      hotelId,
      surname,
      reservationId,
    });

    const data = response.data;

    // If backend returned tokens (single user case), persist them and update context
    if (data.accessToken && data.refreshToken && data.user) {
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: data.user, token: data.accessToken, refreshToken: data.refreshToken },
      });
    }

    // Always return raw payload so callers can inspect guests/reservation or user/tokens
    return data;
  };

  const selectGuest = async (
    hotelId: string,
    reservationId: string,
    profileId: string,
    surname = ''
  ): Promise<any> => {
    const response = await api.post('/auth/select-guest', {
      hotelId,
      reservationId,
      profileId,
      surname,
    });

    const data = response.data;

    if (data.accessToken && data.refreshToken && data.user) {
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: data.user, token: data.accessToken, refreshToken: data.refreshToken },
      });
    }

    return data;
  };

  const selectGuestFromDeepLink = async (
    hotelId: string,
    reservationId: string,
    profileId: string
  ): Promise<any> => {
    const response = await api.post('/auth/deep-link/select-guest', {
      hotelId,
      reservationId,
      profileId,
    });

    const data = response.data;

    if (data.accessToken && data.refreshToken && data.user) {
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: data.user, token: data.accessToken, refreshToken: data.refreshToken },
      });
    }

    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (userData: any) => {
    dispatch({ type: 'REGISTER_START' });
    try {
      const response = await api.post('/auth/register', userData);
      const { user, accessToken, refreshToken } = response.data;
      
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: { user, token: accessToken, refreshToken },
      });
      return Promise.resolve();
    } catch (error) {
      dispatch({ type: 'REGISTER_FAILURE' });
      return Promise.reject(error);
    }
  };

  const refreshAccessToken = async (): Promise<string> => {
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/refresh', { refreshToken: currentRefreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      dispatch({
        type: 'TOKEN_REFRESHED',
        payload: { token: accessToken, refreshToken: newRefreshToken },
      });

      return accessToken;
    } catch (error) {
      logout();
      throw error;
    }
  };

  const refreshUserProfile = async (): Promise<User | null> => {
    try {
      const response = await api.get('/auth/profile');
      if (response.data) {
        dispatch({ type: 'USER_UPDATED', payload: { user: response.data } });
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('[AuthContext] Failed to refresh user profile:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    selectGuest,
    selectGuestFromDeepLink,
    logout,
    register,
    refreshAccessToken,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
